










































import IncidentCard from '@/app/components/monitoring/IncidentCard.vue';
import { useQueryParams } from '@/app/composable';
import { useRouter } from '@/app/composable/router';
import { S } from '@/app/utilities';
import { useAxios } from '@/app/composable';
import { HashtagIcon, ChipIcon } from '@vue-hero-icons/solid';
import { Icon } from '@iconify/vue2';
import { defineComponent, onBeforeUnmount, PropType, ref, computed } from '@vue/composition-api';
import { AxiosResponse } from 'axios';
import { isNil } from 'ramda';
import { MetricsAPI } from '../../api';
import { Asset } from '../../types';
import { AssetMetricsValues, BasicAssetMetricItem } from './../../types/data-profiling.type';
import AssetHistoricalMetrics from './AssetHistoricalMetrics.vue';
import AssetTable from './AssetTable.vue';

export default defineComponent({
    name: 'AssetMetrics',
    props: {
        asset: {
            type: Object as PropType<Asset>,
            required: true,
        },
        columns: {
            type: Array as PropType<{ name: string; type?: string }[]>,
            default: () => [],
        },
        isStreaming: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        HashtagIcon,
        IncidentCard,
        AssetHistoricalMetrics,
        AssetTable,
        ChipIcon,
        Icon,
    },
    setup(props: any, { emit, root }: { emit: any; root: any }) {
        const { exec, loading } = useAxios(true);
        const assetMetricsValues = ref<AssetMetricsValues | undefined>(undefined);

        const router = useRouter();
        const { get, set } = useQueryParams(root, router, 'assets:view');

        const assetMetricsDataProfiling = ref<BasicAssetMetricItem[]>([
            {
                key: 'numOfColumns',
                title: 'Columns',
                message: 'The total number of columns of the asset.',
                colour: 'bg-red-600',
            },
            {
                key: 'numOfObservations',
                title: 'Observations',
                message: 'The total number of rows of the asset. ',
                colour: 'bg-yellow-600',
            },
            {
                key: 'percentageOfMissingCells',
                title: 'Missing Cells',
                message: 'Percentage of missing cells in the asset.',
                colour: 'bg-blue-600',
            },
            {
                key: 'memorySize',
                title: 'Memory Size',
                message: 'Memory size of the data (approximation).',
                colour: 'bg-green-600',
            },
        ]);

        const fetchAssetMetrics = () => {
            if (isNil(props.asset.id)) return;

            exec(MetricsAPI.getAssetMetrics(+props.asset.id))
                .then((res: AxiosResponse<AssetMetricsValues> | undefined) => {
                    if (!res) return;
                    assetMetricsValues.value = res.data;
                    emit('set-last-update', res.data.calculatedAt);
                    emit('new-exploratory-metrics', false);
                })
                .catch((e) => {
                    if (e.response.status !== 403) {
                        (root as any).$toastr.e(
                            'You do not have access to view the specific asset.',
                            'Access Forbidden!',
                        );
                        emit('back-to-assets');
                    } else {
                        (root as any).$toastr.e('Failed to load asset historical metrics', 'Error');
                    }
                });
        };

        fetchAssetMetrics();

        const percentageOfMissingCellsDisplay = computed(() => {
            if (!assetMetricsValues.value) return '0.00';
            const { percentageOfMissingCells } = assetMetricsValues.value;
            return (percentageOfMissingCells * 100).toFixed(2);
        });

        onBeforeUnmount(() => {
            // Clear query params of this tab
            const queryParams: string[] = ['range', 'start', 'end', 'limit'];
            queryParams.forEach((p: string) => {
                const paramValue = get(p);
                if (!isNil(paramValue) && paramValue?.length > 0) set(p, null);
            });
        });

        return {
            S,
            loading,
            assetMetricsDataProfiling,
            assetMetricsValues,
            percentageOfMissingCellsDisplay,
        };
    },
});
